import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertTriangle,
  Users,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AdminLayout, { AdminPageHeader } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface HcpProfile {
  userId: string;
  role: string;
  workEmail: string | null;
  workEmailVerified: boolean;
  organisationName: string | null;
  siteName: string | null;
  teamCode: string | null;
  jobRole: string | null;
  band: string | null;
  registrationNumber: string | null;
  registrationBody: string | null;
  registrationVerified: boolean;
  registrationVerifiedAt: string | null;
  manualReviewRequested: boolean;
  onboardingComplete: boolean;
  approvalStatus: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
}

const ROLE_LABELS: Record<string, string> = {
  nurse: "Nurse",
  doctor: "Doctor",
  pharmacist: "Pharmacist",
  physio: "Physiotherapist",
  ot: "Occupational Therapist",
};

const REG_BODY_LABELS: Record<string, string> = {
  nmc: "NMC",
  gmc: "GMC",
  hcpc: "HCPC",
  gphc: "GPhC",
  none: "None",
};

type Tab = "pending" | "all";

function VerificationBadge({ verified, pending }: { verified: boolean; pending: boolean }) {
  if (verified) {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 font-medium gap-1">
        <CheckCircle2 className="h-3 w-3" /> Verified
      </Badge>
    );
  }
  if (pending) {
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200 font-medium gap-1">
        <Clock className="h-3 w-3" /> Pending review
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-500 border-slate-200 font-medium gap-1">
      <XCircle className="h-3 w-3" /> Not verified
    </Badge>
  );
}

function ApprovalStatusBadge({ status }: { status: "pending" | "approved" | "rejected" }) {
  if (status === "approved") {
    return (
      <Badge className="bg-teal-100 text-teal-700 border-teal-200 font-medium gap-1">
        <CheckCircle2 className="h-3 w-3" /> Access granted
      </Badge>
    );
  }
  if (status === "rejected") {
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200 font-medium gap-1">
        <XCircle className="h-3 w-3" /> Access rejected
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200 font-medium gap-1">
      <Clock className="h-3 w-3" /> Awaiting approval
    </Badge>
  );
}

function HcpRow({
  profile,
  onApprove,
  onReject,
  onReVerify,
  isActing,
}: {
  profile: HcpProfile;
  onApprove: (userId: string, note: string) => void;
  onReject: (userId: string, note: string, reason: string) => void;
  onReVerify: (userId: string) => void;
  isActing: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-200">
              {ROLE_LABELS[profile.role] ?? profile.role}
            </span>
            <ApprovalStatusBadge status={profile.approvalStatus} />
            <VerificationBadge
              verified={profile.registrationVerified}
              pending={profile.manualReviewRequested}
            />
            {profile.onboardingComplete && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 font-medium gap-1">
                <CheckCircle2 className="h-3 w-3" /> Onboarded
              </Badge>
            )}
          </div>
          <p className="text-xs font-mono text-slate-500 truncate">{profile.userId}</p>
          {profile.organisationName && (
            <p className="text-xs text-slate-600 mt-0.5 truncate">
              {profile.organisationName}
              {profile.teamCode && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[10px] border border-slate-200">
                  Team {profile.teamCode}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="text-xs text-slate-400 shrink-0 hidden sm:block">
          {new Date(profile.updatedAt).toLocaleDateString("en-GB")}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Work email</p>
              <p className="text-slate-800">
                {profile.workEmail ?? <span className="text-slate-400 italic">Not set</span>}
                {profile.workEmail && (
                  <span className={`ml-2 text-xs ${profile.workEmailVerified ? "text-green-600" : "text-amber-600"}`}>
                    {profile.workEmailVerified ? "✓ Verified" : "✗ Unverified"}
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Organisation</p>
              <p className="text-slate-800">{profile.organisationName ?? <span className="text-slate-400 italic">Not set</span>}</p>
              {profile.siteName && (
                <p className="text-xs text-slate-500 mt-0.5">{profile.siteName}</p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Team code</p>
              <p className="text-slate-800 font-mono">
                {profile.teamCode ?? <span className="text-slate-400 italic font-sans">Not set</span>}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Job role / band</p>
              <p className="text-slate-800">
                {[profile.jobRole, profile.band].filter(Boolean).join(" — ") || <span className="text-slate-400 italic">Not set</span>}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Registration</p>
              <p className="text-slate-800">
                {profile.registrationBody
                  ? `${REG_BODY_LABELS[profile.registrationBody] ?? profile.registrationBody} ${profile.registrationNumber ?? ""}`
                  : <span className="text-slate-400 italic">Not provided</span>}
              </p>
            </div>
            {profile.registrationVerifiedAt && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-0.5">Verified at</p>
                <p className="text-slate-800 text-xs">
                  {new Date(profile.registrationVerifiedAt).toLocaleString("en-GB")}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-0.5">Registered</p>
              <p className="text-slate-800 text-xs">
                {new Date(profile.createdAt).toLocaleString("en-GB")}
              </p>
            </div>
          </div>

          {profile.approvalStatus === "approved" ? (
            <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-teal-700 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {profile.registrationVerified
                  ? "Access granted. Registration verified."
                  : "Access granted (admin override — registration not currently verified)."}
              </p>
              {profile.registrationNumber && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReVerify(profile.userId)}
                  disabled={isActing}
                  className="gap-1.5 text-slate-600 border-slate-200 hover:bg-slate-50 rounded-lg shrink-0"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Re-verify
                </Button>
              )}
            </div>
          ) : (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              {profile.approvalStatus === "rejected" && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                  <XCircle className="h-4 w-4 shrink-0" />
                  Access has been rejected. You can approve below if circumstances have changed.
                </div>
              )}
              {profile.registrationNumber && (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="text-xs text-slate-600">
                    Re-run automated check against{" "}
                    <span className="font-semibold">{(REG_BODY_LABELS[profile.registrationBody ?? ""] ?? profile.registrationBody ?? "register").toUpperCase()}</span>
                    {" "}register now
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReVerify(profile.userId)}
                    disabled={isActing}
                    className="ml-3 gap-1.5 text-teal-700 border-teal-200 hover:bg-teal-50 rounded-lg shrink-0"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Re-verify
                  </Button>
                </div>
              )}
              {profile.approvalStatus !== "rejected" && (
                <div>
                  <label htmlFor={`rejection-reason-${profile.userId}`} className="text-xs font-medium text-red-600 block mb-1">
                    Reason (shown to applicant) — required for rejection
                  </label>
                  <textarea
                    id={`rejection-reason-${profile.userId}`}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="e.g. Registration number not found on the NMC register. Please check and resubmit."
                    rows={2}
                    className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                  />
                </div>
              )}
              <div>
                <label htmlFor={`admin-note-${profile.userId}`} className="text-xs font-medium text-slate-600 block mb-1">
                  Admin note (optional — internal, logged to audit trail only)
                </label>
                <textarea
                  id={`admin-note-${profile.userId}`}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Checked NMC register on 17 May 2026 — no matching entry found…"
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onApprove(profile.userId, note)}
                  disabled={isActing}
                  className="bg-green-600 hover:bg-green-700 text-white gap-1.5 rounded-lg"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {profile.approvalStatus === "rejected" ? "Approve (override rejection)" : "Approve"}
                </Button>
                {profile.approvalStatus !== "rejected" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReject(profile.userId, note, rejectionReason)}
                    disabled={isActing || !rejectionReason.trim()}
                    className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5 rounded-lg disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminHcpVerifications() {
  const [tab, setTab] = useState<Tab>("pending");
  const [teamFilter, setTeamFilter] = useState("");
  const [sortByTeam, setSortByTeam] = useState(false);
  const [actingUserId, setActingUserId] = useState<string | null>(null);
  const qc = useQueryClient();

  const queryKey = ["admin", "hcp-verifications", tab];

  const { data: profiles = [], isLoading, error, refetch } = useQuery<HcpProfile[]>({
    queryKey,
    queryFn: async () => {
      const url = tab === "pending"
        ? `${basePath}/api/admin/hcp-verifications?filter=pending`
        : `${basePath}/api/admin/hcp-verifications`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch HCP verifications");
      return res.json() as Promise<HcpProfile[]>;
    },
  });

  const approve = useMutation({
    mutationFn: async ({ userId, note }: { userId: string; note: string }) => {
      const res = await fetch(`${basePath}/api/admin/hcp-verifications/${userId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note: note || undefined }),
      });
      if (!res.ok) throw new Error("Approve failed");
      return res.json();
    },
    onMutate: ({ userId }) => setActingUserId(userId),
    onSettled: () => {
      setActingUserId(null);
      qc.invalidateQueries({ queryKey: ["admin", "hcp-verifications"] });
    },
  });

  const reject = useMutation({
    mutationFn: async ({ userId, note, reason }: { userId: string; note: string; reason: string }) => {
      const res = await fetch(`${basePath}/api/admin/hcp-verifications/${userId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note: note || undefined, reason: reason || undefined }),
      });
      if (!res.ok) throw new Error("Reject failed");
      return res.json();
    },
    onMutate: ({ userId }) => setActingUserId(userId),
    onSettled: () => {
      setActingUserId(null);
      qc.invalidateQueries({ queryKey: ["admin", "hcp-verifications"] });
    },
  });

  const reVerify = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`${basePath}/api/admin/hcp-verifications/${userId}/re-verify`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? "Re-verification failed");
      }
      return res.json();
    },
    onMutate: (userId) => setActingUserId(userId),
    onSettled: () => {
      setActingUserId(null);
      qc.invalidateQueries({ queryKey: ["admin", "hcp-verifications"] });
    },
  });

  const pendingCount = profiles.filter((p) => p.approvalStatus === "pending").length;

  const teamFilterTrim = teamFilter.trim().toLowerCase();
  const filteredProfiles = teamFilterTrim
    ? profiles.filter((p) => (p.teamCode ?? "").toLowerCase().startsWith(teamFilterTrim))
    : profiles;
  const displayedProfiles = sortByTeam
    ? [...filteredProfiles].sort((a, b) => {
        const at = a.teamCode ?? "";
        const bt = b.teamCode ?? "";
        if (at && !bt) return -1;
        if (!at && bt) return 1;
        return at.localeCompare(bt);
      })
    : filteredProfiles;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="HCP Verifications"
        description="Review and approve or reject healthcare professional verification requests"
        action={
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit mb-6">
        <button
          onClick={() => setTab("pending")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "pending" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Clock className="h-4 w-4" />
          Pending review
          {tab !== "pending" && (
            <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
              {profiles.filter((p) => p.approvalStatus === "pending").length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("all")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Users className="h-4 w-4" />
          All HCPs
        </button>
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <label htmlFor="team-filter" className="text-xs font-medium text-slate-500">
          Team code
        </label>
        <input
          id="team-filter"
          type="text"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          placeholder="e.g. RJ1"
          className="h-8 text-xs font-mono w-36 px-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-400"
        />
        <button
          type="button"
          onClick={() => setSortByTeam((v) => !v)}
          className={`text-xs px-2 py-1 rounded-md border ${
            sortByTeam
              ? "border-teal-300 bg-teal-50 text-teal-700"
              : "border-slate-200 text-slate-500 hover:text-slate-700"
          }`}
        >
          Sort by team {sortByTeam ? "▾" : "▴"}
        </button>
        {(teamFilter || sortByTeam) && (
          <button
            type="button"
            onClick={() => { setTeamFilter(""); setSortByTeam(false); }}
            className="text-xs text-slate-400 hover:text-slate-700 underline"
          >
            clear
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-slate-400">
          <UserCheck className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Loading…</p>
        </div>
      ) : error ? (
        <div className="text-center py-16 text-slate-500">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-amber-400" />
          <p className="font-medium">Failed to load verification queue</p>
          <p className="text-sm mt-1">Please refresh and try again.</p>
        </div>
      ) : displayedProfiles.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <UserCheck className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="font-medium text-slate-600">
            {tab === "pending" ? "No pending verification requests" : "No HCP profiles found"}
          </p>
          <p className="text-sm mt-1">
            {tab === "pending"
              ? "HCPs whose automated verification failed will appear here after requesting manual review."
              : "HCP profiles will appear here once users complete onboarding."}
          </p>
        </div>
      ) : (
        <>
          {tab === "pending" && pendingCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 mb-4 text-sm text-amber-800">
              <Clock className="h-4 w-4 shrink-0 text-amber-500" />
              <p>
                <strong>{pendingCount}</strong> HCP{pendingCount !== 1 ? "s" : ""} awaiting manual verification.
                Review each request carefully before approving or rejecting.
              </p>
            </div>
          )}
          <p className="text-xs text-slate-400 mb-3">
            {displayedProfiles.length} profile{displayedProfiles.length !== 1 ? "s" : ""} shown
          </p>
          <div className="space-y-3">
            {displayedProfiles.map((profile) => (
              <HcpRow
                key={profile.userId}
                profile={profile}
                onApprove={(userId, note) => approve.mutate({ userId, note })}
                onReject={(userId, note, reason) => reject.mutate({ userId, note, reason })}
                onReVerify={(userId) => reVerify.mutate(userId)}
                isActing={actingUserId === profile.userId}
              />
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
